/** Área de recorte em pixels da imagem original — o que o `react-easy-crop` devolve em `onCropComplete`. */
export interface AreaDeRecorte {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Os dois formatos que o recorte sabe gerar. */
export type MimeDeSaida = 'image/png' | 'image/jpeg';

/**
 * Tamanho e codificação do arquivo que sai do recorte.
 *
 * A saída é **fixa**, nunca derivada do zoom: o servidor exige largura mínima
 * (`larguraMinima` do perfil) e quem enquadra pode ter dado zoom em uma imagem
 * pequena, deixando a área com menos pixels que o mínimo. Rasterizar sempre no
 * tamanho do guia — 1200×400 para o logo 3:1, 800×800 para o 1:1 — faz o
 * arquivo passar independente do que foi escolhido na tela.
 */
export interface SaidaDoRecorte {
  largura: number;
  altura: number;
  mime: MimeDeSaida;
}

/**
 * PNG quando a origem pode ter transparência, JPEG quando não.
 *
 * ⚠️ Logo de parceiro quase sempre chega em PNG com fundo transparente — o
 * `bg-white` do carrossel e do card existe por causa disso. Reencodar para
 * JPEG achataria o transparente em preto e devolveria um retângulo sujo no
 * lugar do logo. Já foto de câmera em PNG sem perdas passaria fácil do teto de
 * 2 MB, e aí JPEG é a escolha certa.
 */
export function mimeDeSaida(tipoDeOrigem: string): MimeDeSaida {
  return tipoDeOrigem === 'image/png' || tipoDeOrigem === 'image/webp'
    ? 'image/png'
    : 'image/jpeg';
}

/** Troca a extensão do nome original pela do formato que sai do recorte. */
export function nomeDoRecorte(nomeOriginal: string, mime: MimeDeSaida): string {
  const semExtensao = nomeOriginal.replace(/\.[^.]+$/, '') || 'imagem';
  return `${semExtensao}.${mime === 'image/png' ? 'png' : 'jpg'}`;
}

function carregarImagem(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.addEventListener('load', () => resolve(imagem));
    imagem.addEventListener('error', reject);
    // A prévia vem de `URL.createObjectURL` do próprio arquivo escolhido —
    // nunca de outra origem — então CORS não entra em jogo aqui.
    imagem.crossOrigin = 'anonymous';
    imagem.src = src;
  });
}

/**
 * Quanto dois canais podem diferir e ainda contarem como "a mesma cor", em
 * 0-255. Folgado o bastante para o ruído de um JPEG salvo em qualidade média,
 * apertado o bastante para não confundir dois tons de cinza distintos.
 */
const TOLERANCIA_DE_COR = 10;

/** Alfa abaixo disto é fundo transparente, não uma cor clara. */
const ALFA_MINIMO = 16;

/**
 * A cor de fundo da imagem, lida da própria imagem — ou `null` quando ela não
 * tem uma cor de fundo única.
 *
 * ⚠️ ISTO EXISTE PARA A COSTURA NÃO APARECER. Ao encaixar, a sobra nas laterais
 * é pintada por nós, e chutar `#ffffff` erra quase sempre: logo exportado do
 * manual de marca costuma vir sobre um branco levemente sujo (#F7F7F7 e
 * parentes), e branco puro ao lado disso vira uma faixa visível de emenda.
 * Amostrando a borda da imagem, a sobra continua a imagem.
 *
 * Só as bordas são lidas — é ali que a sobra encosta. Se elas discordarem
 * (foto, arte que sangra até o canto), não existe cor de fundo para copiar e
 * quem chamou decide o que fazer.
 */
function corDeFundo(imagem: HTMLImageElement): string | null {
  const l = imagem.naturalWidth;
  const a = imagem.naturalHeight;
  if (!l || !a) return null;

  // Cantos e meios de cada lado, um pixel para dentro da borda: em imagem
  // gerada por exportador, a linha exata da borda às vezes sai com meio pixel
  // de antialias.
  const ultimoX = l - 2 > 0 ? l - 2 : 0;
  const ultimoY = a - 2 > 0 ? a - 2 : 0;
  const meioX = Math.floor(l / 2);
  const meioY = Math.floor(a / 2);
  const pontos: readonly (readonly [number, number])[] = [
    [1, 1],
    [ultimoX, 1],
    [1, ultimoY],
    [ultimoX, ultimoY],
    [meioX, 1],
    [meioX, ultimoY],
    [1, meioY],
    [ultimoX, meioY],
  ];

  // Cada amostra é desenhada num pixel próprio de um canvas 8×1 — um
  // `getImageData` só, sem alocar a imagem inteira em memória.
  const amostras = document.createElement('canvas');
  amostras.width = pontos.length;
  amostras.height = 1;
  const contexto = amostras.getContext('2d', { willReadFrequently: true });
  if (!contexto) return null;

  pontos.forEach(([x, y], i) => contexto.drawImage(imagem, x, y, 1, 1, i, 0, 1, 1));

  let dados: Uint8ClampedArray;
  try {
    dados = contexto.getImageData(0, 0, pontos.length, 1).data;
  } catch {
    // Canvas contaminado. Não acontece com `blob:` da mesma origem, mas ler
    // pixel é a única operação aqui que pode lançar por segurança.
    return null;
  }

  const cores = pontos.map((_, i) => ({
    r: dados[i * 4] ?? 0,
    v: dados[i * 4 + 1] ?? 0,
    b: dados[i * 4 + 2] ?? 0,
    alfa: dados[i * 4 + 3] ?? 0,
  }));

  // Transparente é fundo legítimo: devolvido como `null` para o PNG manter o
  // alfa em vez de ganhar uma cor chapada.
  if (cores.every((cor) => cor.alfa < ALFA_MINIMO)) return null;
  if (cores.some((cor) => cor.alfa < 255)) return null;

  const referencia = cores[0];
  if (!referencia) return null;

  const uniforme = cores.every(
    (cor) =>
      Math.abs(cor.r - referencia.r) <= TOLERANCIA_DE_COR &&
      Math.abs(cor.v - referencia.v) <= TOLERANCIA_DE_COR &&
      Math.abs(cor.b - referencia.b) <= TOLERANCIA_DE_COR,
  );
  if (!uniforme) return null;

  return `rgb(${referencia.r}, ${referencia.v}, ${referencia.b})`;
}

/**
 * Recorta `area` da imagem em `imagemSrc` e devolve um blob no tamanho exato
 * pedido em `saida` — sempre o mesmo tamanho independente do zoom e da posição
 * escolhidos, pelos motivos descritos em `SaidaDoRecorte`.
 *
 * `area` pode ser MAIOR que a imagem, com x/y negativos: é o que o
 * `react-easy-crop` devolve quando o zoom desce abaixo de 1 para encaixar um
 * logo inteiro num quadro mais largo que ele. O `drawImage` recorta o
 * retângulo de origem contra os limites da imagem e encolhe o destino na mesma
 * proporção, então a imagem cai centrada e a sobra fica com o fundo — sem
 * conta nenhuma aqui.
 */
export async function recortarImagem(
  imagemSrc: string,
  area: AreaDeRecorte,
  saida: SaidaDoRecorte,
): Promise<Blob> {
  const imagem = await carregarImagem(imagemSrc);
  const canvas = document.createElement('canvas');
  canvas.width = saida.largura;
  canvas.height = saida.altura;

  const contexto = canvas.getContext('2d');
  if (!contexto) throw new Error('Canvas 2D indisponível.');

  // A área escolhida pode ter menos pixels que a saída (zoom em imagem
  // pequena): aí isto aqui é ampliação, e sem suavização boa sai serrilhado.
  contexto.imageSmoothingEnabled = true;
  contexto.imageSmoothingQuality = 'high';

  // O fundo da sobra do encaixe, na ordem que menos aparece:
  //  1. a cor da borda da própria imagem, quando ela tem uma — a sobra vira
  //     continuação do arquivo e não existe emenda;
  //  2. nada, deixando transparente, quando a origem é transparente e a saída
  //     é PNG (toda superfície que exibe estes ativos já é branca, e assim o
  //     logo continua servindo para outro fundo depois);
  //  3. branco, só no JPEG sem cor de borda — JPEG não tem alfa, e sem esta
  //     pintura o transparente viraria PRETO.
  const fundo = corDeFundo(imagem) ?? (saida.mime === 'image/jpeg' ? '#ffffff' : null);
  if (fundo) {
    contexto.fillStyle = fundo;
    contexto.fillRect(0, 0, saida.largura, saida.altura);
  }

  contexto.drawImage(
    imagem,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    saida.largura,
    saida.altura,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Falha ao gerar a imagem.'))),
      saida.mime,
      // Ignorado em PNG; 0.9 é o ponto onde o JPEG ainda não mostra artefato.
      0.9,
    );
  });
}
